import { jsx as _jsx } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import { blue, blueGrey, cyan, deepOrange, deepPurple, green, lime, pink, yellow } from '@mui/material/colors';
import * as React from 'react';
import { Box } from '../box';
import { Button } from '../button';
import { ColorIndicator } from '../color-indicator';
import { Popover, usePopoverProps } from '../popover';
import { TextField } from '../textfield';
const SketchPicker = React.lazy(() => import('./sketch-picker'));
const storage = (() => {
    const STORAGE_KEY = 'ml-webclient-styleguide-color-picker-colors';
    const getColors = () => {
        const colors = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return colors.slice(0, 10);
    };
    return {
        getColors,
        setColor: (color) => {
            const colorLower = color.toLowerCase();
            const colors = getColors();
            if (![...colors, ...PRESET_COLORS].includes(colorLower)) {
                const newColors = [colorLower, ...colors].slice(0, 10);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(newColors));
            }
        }
    };
})();
const PRESET_COLORS = [
    blueGrey[500],
    blue[600],
    cyan[600],
    deepPurple[300],
    deepPurple[600],
    pink[400],
    deepOrange[500],
    yellow[400],
    lime[300],
    green[500]
];
const Label = (props) => {
    const { value, onChange, variant, label, ...labelProps } = props;
    const color = _jsx(ColorIndicator, { color: props.value });
    const slotProps = {
        input: value ? { startAdornment: color } : undefined
    };
    switch (variant) {
        case 'default':
        default:
            return (_jsx(Button, { ...labelProps, variant: "outlined", color: "inherit", sx: {
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    py: 0,
                    px: 0,
                    borderColor: (theme) => theme.components?.input?.outlined?.['enabled-border']
                }, children: color }));
        case 'expanded':
            return (_jsx(TextField, { ...labelProps, value: value, label: label, sx: {
                    '.MuiInputBase-root': {
                        gap: 1
                    }
                }, slotProps: slotProps, onChange: (newValue) => onChange?.(newValue) }));
    }
};
export const ColorPicker = (props) => {
    const { typography, shape } = useTheme();
    const popoverProps = usePopoverProps({
        label: Label,
        labelProps: props,
        labelWrapperProps: {
            width: props.variant === 'default' ? 'max-content' : '100%'
        }
    });
    return (_jsx(Popover, { disableAutoFocus: props.variant === 'expanded', transformOrigin: { horizontal: 'left' }, anchorOrigin: { horizontal: 'left' }, ...popoverProps, onClose: () => {
            storage.setColor(props.value);
            popoverProps.onClose?.();
        }, children: _jsx(Box, { sx: {
                '.sketch-picker': {
                    fontFamily: typography.fontFamily,
                    fontSize: typography.fontSize
                },
                '.sketch-picker label, .sketch-picker input': {
                    fontSize: `${typography.fontSize}px !important`,
                    borderRadius: `${shape.borderRadius}px`
                },
                '.sketch-picker label, .sketch-picker input:focus-visible': {
                    outlineColor: (theme) => theme.palette.primary.main
                },
                // Workaround: modify colors of the color picker
                '.flexbox-fix:last-child': {
                    justifyContent: 'space-around',
                    'div[title]': {
                        borderRadius: '100% !important'
                    }
                }
            }, children: _jsx(React.Suspense, { fallback: null, children: _jsx(SketchPicker, { color: props.value, presetColors: [
                        ...storage.getColors(),
                        ...PRESET_COLORS
                    ].slice(0, 10), onChange: (c) => props.onChange?.(c.hex), disableAlpha: true, width: "284px" }) }) }) }));
};
ColorPicker.PRESET_COLORS = PRESET_COLORS;
